import { useState, useEffect } from "react";
import type { OwnershipType } from "@/types/domain";
import { Button } from "@/components/ui/button";
import { ToggleGroup as OwnershipToggle, ToggleGroupItem as OwnershipToggleItem } from "@/components/ui/toggle-group";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Ban, Save, ChevronRight } from "lucide-react";

interface ClassificationRow {
  source: "satellite" | "website";
  has_sport_field: boolean | null;
  user_has_sport_field: boolean | null;
  user_notes?: string | null;
}

interface ClassificationFormProps {
  locationId: string;
  classifications: ClassificationRow[];
  ownership: OwnershipType;
  userNotes: string | null;
  visibilityRating: number | null;
  isCulturalSpot: boolean;
  approvedByCity: boolean;
  approvedBySponsor: boolean;
  ownershipConfirmed: boolean;
  isRejected: boolean;
  onSave: (data: {
    userHasSportField: boolean | null;
    userNotes: string | null;
    ownership: OwnershipType;
    visibilityRating: number | null;
    isCulturalSpot: boolean;
    approvedByCity: boolean;
    approvedBySponsor: boolean;
    ownershipConfirmed: boolean;
  }) => void;
  onReject: () => void;
  onUnreject: () => void;
  onChange?: (data: {
    hasSportField: boolean | null;
    ownership: OwnershipType;
  }) => void;
  onFormDataChange?: (data: {
    userHasSportField: boolean | null;
    userNotes: string | null;
    ownership: OwnershipType;
    visibilityRating: number | null;
    isCulturalSpot: boolean;
    approvedByCity: boolean;
    approvedBySponsor: boolean;
    ownershipConfirmed: boolean;
  }) => void;
  isSaving: boolean;
  isRejecting: boolean;
  hideActions?: boolean;
}

export function ClassificationForm({
  locationId,
  classifications,
  ownership,
  userNotes,
  visibilityRating: initVisibilityRating,
  isCulturalSpot: initIsCulturalSpot,
  approvedByCity: initApprovedByCity,
  approvedBySponsor: initApprovedBySponsor,
  ownershipConfirmed: initOwnershipConfirmed,
  isRejected,
  onSave,
  onReject,
  onUnreject,
  onChange,
  onFormDataChange,
  isSaving,
  isRejecting,
  hideActions,
}: ClassificationFormProps) {
  // Derive initial values from satellite classification
  const satellite = classifications.find((c) => c.source === "satellite");
  const initHasSportField = satellite?.user_has_sport_field ?? satellite?.has_sport_field ?? null;
  const initNotes = userNotes ?? satellite?.user_notes ?? "";

  const [hasSportField, setHasSportField] = useState<boolean | null>(initHasSportField);
  const [ownershipValue, setOwnershipValue] = useState<OwnershipType>(ownership);
  const [notes, setNotes] = useState(initNotes);
  const [visibilityRating, setVisibilityRating] = useState<number | null>(initVisibilityRating);
  const [isCulturalSpot, setIsCulturalSpot] = useState(initIsCulturalSpot);
  const [approvedByCity, setApprovedByCity] = useState(initApprovedByCity);
  const [approvedBySponsor, setApprovedBySponsor] = useState(initApprovedBySponsor);
  const [ownershipConfirmed, setOwnershipConfirmed] = useState(initOwnershipConfirmed);
  const [advancedOpen, setAdvancedOpen] = useState(true);

  // Emit full form data whenever any field changes
  useEffect(() => {
    onFormDataChange?.({
      userHasSportField: hasSportField,
      userNotes: notes || null,
      ownership: ownershipValue,
      visibilityRating,
      isCulturalSpot,
      approvedByCity,
      approvedBySponsor,
      ownershipConfirmed,
    });
  }, [hasSportField, ownershipValue, notes, visibilityRating, isCulturalSpot, approvedByCity, approvedBySponsor, ownershipConfirmed]);

  // Reset form when locationId changes
  useEffect(() => {
    const sat = classifications.find((c) => c.source === "satellite");
    setHasSportField(sat?.user_has_sport_field ?? sat?.has_sport_field ?? null);
    setOwnershipValue(ownership);
    setNotes(userNotes ?? sat?.user_notes ?? "");
    setVisibilityRating(initVisibilityRating);
    setIsCulturalSpot(initIsCulturalSpot);
    setApprovedByCity(initApprovedByCity);
    setApprovedBySponsor(initApprovedBySponsor);
    setOwnershipConfirmed(initOwnershipConfirmed);
  }, [locationId]);

  const handleSave = () => {
    onSave({
      userHasSportField: hasSportField,
      userNotes: notes || null,
      ownership: ownershipValue,
      visibilityRating,
      isCulturalSpot,
      approvedByCity,
      approvedBySponsor,
      ownershipConfirmed,
    });
  };

  return (
    <div className="space-y-4">
      {/* Sport field toggle */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Sport Field</label>
        <ToggleGroup
          type="single"
          variant="outline"
          value={hasSportField === true ? "yes" : hasSportField === false ? "no" : ""}
          onValueChange={(val) => {
            const next = val === "yes" ? true : val === "no" ? false : null;
            setHasSportField(next);
            onChange?.({ hasSportField: next, ownership: ownershipValue });
          }}
        >
          <ToggleGroupItem value="yes" className="px-4">Yes</ToggleGroupItem>
          <ToggleGroupItem value="no" className="px-4">No</ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Ownership */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Ownership</label>
        <OwnershipToggle
          type="single"
          variant="outline"
          value={ownershipValue}
          onValueChange={(v) => {
            if (v) {
              setOwnershipValue(v as OwnershipType);
              onChange?.({ hasSportField, ownership: v as OwnershipType });
            }
          }}
          className="flex flex-wrap gap-1"
        >
          <OwnershipToggleItem value="public" className="px-3 text-xs hover:bg-blue-500 hover:text-white data-[state=on]:bg-blue-500 data-[state=on]:text-white data-[state=on]:border-blue-500">Public</OwnershipToggleItem>
          <OwnershipToggleItem value="private" className="px-3 text-xs hover:bg-orange-500 hover:text-white data-[state=on]:bg-orange-500 data-[state=on]:text-white data-[state=on]:border-orange-500">Private</OwnershipToggleItem>
          <OwnershipToggleItem value="institutional" className="px-3 text-xs hover:bg-teal-500 hover:text-white data-[state=on]:bg-teal-500 data-[state=on]:text-white data-[state=on]:border-teal-500">Institutional</OwnershipToggleItem>
          <OwnershipToggleItem value="unknown" className="px-3 text-xs hover:bg-gray-500 hover:text-white data-[state=on]:bg-gray-500 data-[state=on]:text-white data-[state=on]:border-gray-500">Unknown</OwnershipToggleItem>
        </OwnershipToggle>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Notes</label>
        <Textarea
          placeholder="Add notes about this location..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
        />
      </div>

      {/* Advanced */}
      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
          <ChevronRight className={`size-4 transition-transform ${advancedOpen ? "rotate-90" : ""}`} />
          Advanced
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-3">
          {/* Rating */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Rating</Label>
              <span className="text-xs text-muted-foreground tabular-nums">
                {visibilityRating ?? "—"}
              </span>
            </div>
            <Slider
              min={1}
              max={5}
              step={1}
              value={visibilityRating != null ? [visibilityRating] : undefined}
              defaultValue={[3]}
              onValueChange={([v]) => setVisibilityRating(v ?? null)}
            />
          </div>
          {/* Boolean toggles */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="cultural-spot" className="text-sm">Cultural Spot</Label>
              <Switch
                id="cultural-spot"
                checked={isCulturalSpot}
                onCheckedChange={setIsCulturalSpot}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="approved-city" className="text-sm">Approved by City</Label>
              <Switch
                id="approved-city"
                checked={approvedByCity}
                onCheckedChange={setApprovedByCity}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="approved-sponsor" className="text-sm">Approved by Sponsor</Label>
              <Switch
                id="approved-sponsor"
                checked={approvedBySponsor}
                onCheckedChange={setApprovedBySponsor}
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Actions */}
      {!hideActions && (
        <div className="flex gap-2 pt-2">
          <Button onClick={handleSave} disabled={isSaving} className="flex-1">
            <Save className="mr-2 size-4" />
            {isSaving ? "Saving..." : "Save"}
          </Button>
          {isRejected ? (
            <Button
              variant="outline"
              onClick={onUnreject}
              disabled={isRejecting}
            >
              Un-reject
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={onReject}
              disabled={isRejecting}
            >
              <Ban className="mr-2 size-4" />
              Reject
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
